/**
 * ResetPasswordPage — consume a reset token and set a new password.
 *
 * The token comes from the email link's query string (?token=...).
 * Submitting POSTs to /api/auth/reset-password. The backend returns
 * generic 400 errors for any failure (expired, used, unknown) — we
 * surface the message verbatim.
 */

import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { showToast } from "../../lib/toast";

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") ?? "";

  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasToken = token.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasToken) return;

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: newPassword }),
      });
      if (!res.ok) {
        let message = "This reset link is invalid or has expired. Request a new one.";
        try {
          const body = (await res.json()) as { detail?: { message?: string } };
          if (body.detail?.message) message = body.detail.message;
        } catch {
          /* keep default */
        }
        setError(message);
        return;
      }
      showToast({
        variant: "success",
        title: "Password updated",
        description: "You can now sign in with your new password.",
      });
      navigate("/login", { replace: true });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-brand-navy">
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-lg bg-brand-crimson flex items-center justify-center">
              <svg
                className="w-6 h-6 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5"></path>
                <path d="M2 12l10 5 10-5"></path>
              </svg>
            </div>
            <span className="text-3xl font-bold text-white tracking-tight">XYDEN</span>
          </div>
          <p className="text-slate-400 text-sm">Leadership Maturity Assessment Platform</p>
        </div>

        <div className="bg-white rounded-lg shadow-2xl p-8">
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-slate-900">Set a new password</h1>
            <p className="text-sm text-slate-500 mt-1">
              Choose a password of at least 8 characters.
            </p>
          </div>

          {!hasToken && (
            <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm font-medium">
              No reset token found in this link. <Link to="/forgot-password" className="underline">Request a new one</Link>.
            </div>
          )}

          {error && (
            <div
              role="alert"
              aria-live="assertive"
              className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm font-medium"
            >
              {error}
            </div>
          )}

          {hasToken && (
            <form onSubmit={handleSubmit} noValidate aria-label="Reset password" className="space-y-4">
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
                  New password
                </label>
                <input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (error) setError(null);
                  }}
                  className="w-full border border-brand-steel/50 bg-white rounded-lg px-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-crimson/30"
                  placeholder="••••••••"
                  aria-label="New password"
                />
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm new password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => {
                    setConfirm(e.target.value);
                    if (error) setError(null);
                  }}
                  className="w-full border border-brand-steel/50 bg-white rounded-lg px-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-crimson/30"
                  placeholder="••••••••"
                  aria-label="Confirm new password"
                />
              </div>

              <button
                type="submit"
                disabled={!newPassword || !confirm || isLoading}
                className="w-full py-2.5 bg-brand-crimson text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-crimson/90"
              >
                {isLoading ? "Updating..." : "Update password"}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm font-medium text-brand-crimson hover:underline">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}

export default ResetPasswordPage;
